import { oldUserSchema } from "@api/user/schemas";
import type { TTSBlock } from "@components/TTSEditor/types";
import { fetchResource } from "@src/app/utils";
import { z } from "zod";
import { tracklistArtworkSchema, tracklistSchema } from "./schemas";
import type { Tracklist } from "./types";
import { filterTracks } from "./utils";

/**
 * fetch list of currently trending tracklists
 */
export const getTrendingTrackLists = async (page = 1, inDays = 7) => {
	const res = await fetchResource(`trendings/tracklists?page=${page}&in_days=${inDays}`, z.array(tracklistSchema));

	return res.map((tr) => ({ ...tr, tracks: filterTracks(tr.tracks) }));
};

/**
 * Get user's home feed
 */
export const getHomeFeed = async (page: number) => {
	const res = await fetchResource(`feed?page=${page}`, z.array(tracklistSchema), "GET", undefined, {
		apiVersion: "v2",
	});

	return res.map((tr) => ({ ...tr, tracks: filterTracks(tr.tracks) }));
};

/**
 * Rate specific tracklist
 */
export const postTrackListLike = (uuid: Tracklist["uuid"]) =>
	fetchResource(`tracklist/${uuid}/rating`, z.object({}), "POST", undefined, {
		emptyResponse: true,
	});

/**
 * Remove rating from specific tracklist
 */
export const deleteTrackListLike = (uuid: Tracklist["uuid"]) =>
	fetchResource(`tracklist/${uuid}/rating`, z.object({}), "DELETE", undefined, {
		emptyResponse: true,
	});

/**
 * fetch specific tracklist
 */
export const getTrackList = async (uuid: Tracklist["uuid"], filter?: boolean, landing?: boolean) => {
	const res = await fetchResource(`tracklist/${uuid}`, tracklistSchema, "GET", undefined, {
		apiVersion: "v2",
		apiUri: landing ? "https://api.voxtox.fm" : undefined,
	});
	if (!filter) return res;
	return {
		...res,
		tracks: filterTracks(res.tracks),
	};
};

/**
 * Get paginated list of user who rated a tracklist
 */
export const getRatings = (uuid: Tracklist["uuid"], page = 1) =>
	fetchResource(`tracklist/${uuid}/rating?page=${page}`, z.array(oldUserSchema));

/**
 * Share tracklist
 */
export const putShareTrackList = (uuid: Tracklist["uuid"]) =>
	fetchResource(`tracklist/${uuid}/share`, tracklistSchema.pick({ uuid: true }), "PUT");

/**
 * Fetch user's drafts
 */
export const getDrafts = (page: number) => fetchResource(`tracklist/drafts?page=${page}`, z.array(tracklistSchema));

/**
 * Delte specific tracklist
 */
export const deleteTracklist = (uuid: Tracklist["uuid"]) =>
	fetchResource(`tracklist/${uuid}`, z.object({}), "DELETE", undefined, { emptyResponse: true });

/**
 * Un-share tracklist
 */
export const deleteShareTrackList = (uuid: Tracklist["uuid"]) =>
	fetchResource(`tracklist/${uuid}/share`, z.object({}), "DELETE", undefined, { emptyResponse: true });

/**
 * Get scheduled tracklists
 */
export const getScheduledTrackLists = async (page: number) => {
	const res = await fetchResource(`tracklist/scheduled?page=${page}`, z.array(tracklistSchema));
	return res.map((tr) => ({ ...tr, tracks: filterTracks(tr.tracks) }));
};

/**
 * Publish tracklist immediately
 */
export const patchPublishTrackListNow = (tracklist: Tracklist) =>
	fetchResource(`tracklist/${tracklist.uuid}`, tracklistSchema, "PATCH", {
		...tracklist,
		published: true,
		publish_at: null,
		photo: undefined,
		categories: tracklist.categories.map((c) => c.id),
	});

/**
 * post new tts tracklist comment
 */
export const postTTSComment = (data: TTSBlock[], uuid: string) =>
	fetchResource(
		`tracklist/${uuid}/comments/tts`,
		z.object({}),
		"POST",
		{
			data: data.map<Omit<TTSBlock, "id">>((b) => ({ ...b, id: undefined })),
		},
		{ emptyResponse: true },
	);
/**
 * Post voice comment
 * @param data **file**: `Blob`
 */
export const postVoiceComment = (data: FormData, uuid: Tracklist["uuid"]) =>
	fetchResource(`tracklist/${uuid}/comments`, z.object({}), "POST", data, { emptyResponse: true, formData: true });

export const getTracklistArtworks = () => fetchResource("artwork/tracklist", z.array(tracklistArtworkSchema));

export const createTrackList = async (
	tracks: { id: string; platform: string }[],
	tracklistUUID?: string,
	name?: string,
	photo?: string,
	groupUuid?: string | null,
	published?: boolean,
	categories?: number[],
	location?: { lat: number; long: number },
	publishAt?: string,
) => {
	const res = await fetchResource(
		`tracklist${tracklistUUID ? `/${tracklistUUID}` : ""}`,
		tracklistUUID ? z.object({}) : tracklistSchema.pick({ uuid: true }),
		tracklistUUID ? "PATCH" : "POST",
		{
			name,
			tracks,
			photo,
			groupUuid,
			published,
			categories,
			location: location ? { point: { latitude: location.lat, longitude: location.long } } : null,
			publish_at: publishAt ?? null,
		},
		{
			emptyResponse: !!tracklistUUID,
		},
	);
	if (tracklistUUID) return tracklistUUID;
	const parse = tracklistSchema.pick({ uuid: true }).parse(res);
	return parse.uuid;
};

export const patchTracklistPhoto = (uuid: Tracklist["uuid"], image: Blob) => {
	const formData = new FormData();
	formData.set("photo", new Blob([image], { type: "image/jpeg" }));
	return fetchResource(`tracklist/${uuid}/photo`, z.object({}), "PATCH", formData, {
		formData: true,
		emptyResponse: true,
	});
};
